/**
 * PhishTrainer texts that arrive in English from a fixed, code-defined
 * catalogue, shown in the reader's language (locale audit 2026-09-26, L-02;
 * plan wave 3, lane W08).
 *
 * The tier ladder (lib/phishtrainer/types TIER_DEFS), the weekly quest pool
 * (engines/quests-engine QUEST_POOL), the achievement list
 * (engines/achievements-engine) and the P.H.I.S.H. taxonomy (the API's
 * PHISH_TAXONOMY, mirrored in data/phishtrainer/phish-taxonomy.json) are
 * product reference data, not anything a customer wrote, so they are shown in
 * the learner's language like the rest of the card.
 *
 * messages/learner/en.json keeps a copy of each English text under a key built
 * from the entry's stable id. The translation is shown only while the text
 * that arrived is still exactly that English: when the source rewords an entry
 * or adds a new one, the reader sees the new English, never a stale
 * translation of the old wording.
 */
import { ENGLISH_MESSAGES } from '@/lib/i18n/catalogues'
import { translate } from '@/lib/i18n/translate'
import { parseApiDate } from '@/lib/time/parse-api-date'

export type Translate = (key: string, params?: Record<string, string | number>) => string

/**
 * `given` in the reader's language when it is still the catalogue's English
 * for `key`; otherwise `given` exactly as it arrived.
 */
export function catalogueText(t: Translate, key: string, given: string | null | undefined): string {
  const text = given ?? ''
  if (text === '') return text
  return translate(ENGLISH_MESSAGES, ENGLISH_MESSAGES, key) === text ? t(key) : text
}

/** A tier's name ('Rock Star'), by its key ('rockstar'). */
export function tierName(t: Translate, key: string, name: string): string {
  return catalogueText(t, `learner.cards.tiers.${key}`, name)
}

/** A weekly quest's name and description, by quest id. */
export function questText(t: Translate, quest: { id: string; name: string; description: string }) {
  return {
    name: catalogueText(t, `learner.cards.quests.${quest.id}.name`, quest.name),
    description: catalogueText(t, `learner.cards.quests.${quest.id}.description`, quest.description),
  }
}

/** An achievement's name and description, by achievement id. */
export function achievementText(t: Translate, item: { id: string; name: string; desc: string }) {
  return {
    name: catalogueText(t, `learner.cards.achievements.defs.${item.id}.name`, item.name),
    desc: catalogueText(t, `learner.cards.achievements.defs.${item.id}.description`, item.desc),
  }
}

/** A P.H.I.S.H. pillar's texts, by pillar code. */
export function pillarText(
  t: Translate,
  pillar: { code: string; name: string; tagline: string; question: string; keyword: string },
) {
  const base = `learner.cards.taxonomy.pillars.${pillar.code}`
  return {
    name: catalogueText(t, `${base}.name`, pillar.name),
    tagline: catalogueText(t, `${base}.tagline`, pillar.tagline),
    question: catalogueText(t, `${base}.question`, pillar.question),
    keyword: catalogueText(t, `${base}.keyword`, pillar.keyword),
  }
}

/**
 * A P.H.I.S.H. flag's name and reason, by flag id ('pf1-1'). The example is
 * not translated: it quotes the kind of text a simulated email carries, and
 * those emails are not translated either.
 */
export function flagText(t: Translate, flag: { id: string; flag: string; coreReason: string }) {
  const base = `learner.cards.taxonomy.flags.${flag.id}`
  return {
    flag: catalogueText(t, `${base}.flag`, flag.flag),
    reason: catalogueText(t, `${base}.reason`, flag.coreReason),
  }
}

/**
 * A difficulty band the data carries in English ('Easy' | 'Medium' | 'Hard' |
 * 'Expert'); anything else (a caller's own 'T3 · Medium') is shown as given.
 */
export function difficultyLabel(t: Translate, value: string): string {
  switch (value) {
    case 'Easy':
      return t('play.library.difficulty.easy')
    case 'Medium':
      return t('play.library.difficulty.medium')
    case 'Hard':
      return t('play.library.difficulty.hard')
    case 'Expert':
      return t('play.library.difficulty.expert')
    default:
      return value
  }
}

/**
 * "5m ago" / "3h ago" / "2d ago" — the /play relative-time strings. A
 * zone-less timestamp is read as UTC (parseApiDate), never as local time.
 */
export function shortTimeAgo(t: Translate, iso: string | null | undefined, now = Date.now()): string {
  const at = parseApiDate(iso)
  if (!at) return ''
  const mins = Math.round((now - at.getTime()) / 60000)
  if (mins < 60) return t('play.time.minutesAgo', { count: Math.max(1, mins) })
  const hours = Math.round(mins / 60)
  if (hours < 24) return t('play.time.hoursAgo', { count: hours })
  return t('play.time.daysAgo', { count: Math.round(hours / 24) })
}
